/* Small dropdown button that lets an admin preview + download student
 * attestations. Preview opens the generated PDF in an in-app modal (iframe)
 * with Print + Download actions before saving. The reinscription still needs
 * a target class + promotion type input first.
 */
import { useState } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { FileText, GraduationCap, Backpack, Printer, DownloadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const AttestationMenu = ({ student }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [nextClass, setNextClass] = useState('');
  const [nextYear, setNextYear] = useState('');
  const [promotion, setPromotion] = useState('promu');
  const [busy, setBusy] = useState(false);

  // Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewName, setPreviewName] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');

  const openPreview = async (url, filename, title) => {
    setBusy(true);
    try {
      const { data } = await api.get(url, { responseType: 'blob' });
      const objUrl = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      if (previewUrl) window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(objUrl);
      setPreviewName(filename);
      setPreviewTitle(title);
      setPreviewOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la génération');
    } finally { setBusy(false); }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewUrl('');
  };

  const downloadCurrent = () => {
    const a = document.createElement('a');
    a.href = previewUrl;
    a.download = previewName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success('Attestation téléchargée');
  };

  const printCurrent = () => {
    const frame = document.getElementById('attestation-preview-frame');
    try {
      frame?.contentWindow?.focus();
      frame?.contentWindow?.print();
    } catch {
      toast.error('Impression indisponible, utilisez Télécharger');
    }
  };

  const previewScolarite = () =>
    openPreview(
      `/students/${student.id}/attestation-scolarite.pdf`,
      `attestation_scolarite_${student.last_name}_${student.first_name}.pdf`.replace(/ /g, '_'),
      `Attestation de scolarité — ${student.first_name} ${student.last_name}`,
    );

  const openReinscription = () => {
    const cur = student.school_year || '';
    let suggested = '';
    if (cur && cur.includes('-')) {
      const [a] = cur.split('-');
      const y = parseInt(a, 10);
      if (!isNaN(y)) suggested = `${y + 1}-${y + 2}`;
    }
    setNextYear(suggested);
    setNextClass(student.class_id || '');
    setPromotion('promu');
    setDialogOpen(true);
  };

  const submitReinscription = async () => {
    if (!nextClass || !nextYear) {
      toast.error('Renseignez la classe et l\'année scolaire cible');
      return;
    }
    const q = new URLSearchParams({
      next_school_year: nextYear,
      next_class: nextClass,
      promotion_type: promotion,
    }).toString();
    await openPreview(
      `/students/${student.id}/attestation-reinscription.pdf?${q}`,
      `attestation_reinscription_${student.last_name}_${student.first_name}.pdf`.replace(/ /g, '_'),
      `Attestation de réinscription — ${student.first_name} ${student.last_name}`,
    );
    setDialogOpen(false);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost" size="sm"
            className="hover:bg-[#0052FF]/10 text-[#0052FF]"
            title="Attestations"
            data-testid={`attest-btn-${student.id}`}
          >
            <FileText size={18} weight="duotone" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Attestations PDF</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={previewScolarite}
            disabled={busy}
            data-testid={`attest-scolarite-${student.id}`}
          >
            <GraduationCap size={16} className="mr-2 text-[#0052FF]" weight="duotone" />
            Attestation de scolarité
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={openReinscription}
            disabled={busy}
            data-testid={`attest-reinscription-${student.id}`}
          >
            <Backpack size={16} className="mr-2 text-emerald-600" weight="duotone" />
            Attestation de réinscription…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="reinscription-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit">
              Attestation de réinscription
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Année scolaire cible</Label>
              <Input
                value={nextYear}
                onChange={(e) => setNextYear(e.target.value)}
                placeholder="2027-2028"
                data-testid="reinsc-year-input"
              />
            </div>
            <div>
              <Label>Classe cible</Label>
              <Input
                value={nextClass}
                onChange={(e) => setNextClass(e.target.value)}
                placeholder="Ex: 5è A"
                data-testid="reinsc-class-input"
              />
            </div>
            <div>
              <Label>Type de promotion</Label>
              <div className="flex gap-4 mt-1.5">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio" name="promotion" value="promu"
                    checked={promotion === 'promu'}
                    onChange={() => setPromotion('promu')}
                    data-testid="reinsc-promu"
                  />
                  Promu(e)
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio" name="promotion" value="admis"
                    checked={promotion === 'admis'}
                    onChange={() => setPromotion('admis')}
                    data-testid="reinsc-admis"
                  />
                  Admis(e)
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={submitReinscription}
              disabled={busy}
              className="bg-[#0052FF] hover:bg-[#0052FF]/90"
              data-testid="reinsc-download-btn"
            >
              Aperçu
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={previewOpen} onOpenChange={(o) => (o ? setPreviewOpen(true) : closePreview())}>
        <DialogContent className="max-w-4xl" data-testid="attestation-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-base">{previewTitle}</DialogTitle>
          </DialogHeader>
          <div className="rounded-md border border-[#E2E8F0] overflow-hidden bg-[#F8FAFC]">
            {previewUrl && (
              <iframe
                id="attestation-preview-frame"
                title="Aperçu attestation"
                src={previewUrl}
                className="w-full h-[68vh]"
                data-testid="attestation-preview-frame"
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closePreview} data-testid="attestation-preview-close">
              Fermer
            </Button>
            <Button variant="outline" onClick={printCurrent} data-testid="attestation-print-btn">
              <Printer size={16} className="mr-1.5" /> Imprimer
            </Button>
            <Button
              onClick={downloadCurrent}
              className="bg-[#0052FF] hover:bg-[#0052FF]/90"
              data-testid="attestation-download-btn"
            >
              <DownloadSimple size={16} className="mr-1.5" /> Télécharger
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AttestationMenu;
